import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import "./App.css";
import "./global.css";
import "./i18n";
import PageNotFound from "./pages/page-not-found/pageNotFound";
import { useEffect } from "react";
import { getOrgName } from "./commons/functions/url";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { dashboardRoutes, routes } from "./commons/store/navigation/routes";

import LogoutWLS from "./pages/logoutWLS/LogoutWLS";
import LoadingPxch from "./components/LoadingPxch/LoadingPxch/LoadingPxch.tsx";
import VerifyInvite1 from "./pages/page-verify-email/PageVerifyEmaila.tsx";
import { wlsNoMeetService, superAdminRoleName } from "./customize/defaultValues.js";
import Forbidden from "./pages/page-forbidden/ForbiddenPage.js";
import ElevenLabsSDKWidget from "./components/ElevenLabsSDKWidget/ElevenLabsSDKWidget.jsx";
import GHLWidget from "./components/GHLWidget/GHLWidget.jsx";
import { useAuth0 } from "@auth0/auth0-react";

// import PageMaintenance from "./pages/page-maintenance/PageMaintenance";
// import Layout from "./components/layout/Layout";
// import WelcomeHome from "./pages/welcomeHome/WelcomeHome";
// import AccountDisabled from "./components/AccountDisabled/AccountDisabled.js";
// import AccountTrialEnds from "./components/AccountTrialEnds/AccountTrialEnds.js";
// import AccountOnHold from "./components/AccountOnHold/AccountOnHold.js";
// import DashboardMain from "./pages/dasboard/dashboard.js";

const Login = lazy(() => import("./pages/login/login"));
const DashboardMain = lazy(() => import("./pages/dasboard/dashboard"));
const LoginWLS = lazy(() => import("./pages/loginWLS/LoginWLS"));
const AccountOnHold = lazy(() =>
  import("./components/AccountOnHold/AccountOnHold.js")
);
const AccountTrialEnds = lazy(() =>
  import("./components/AccountTrialEnds/AccountTrialEnds.js")
);
const AccountDisabled = lazy(() =>
  import("./components/AccountDisabled/AccountDisabled.js")
);
const WelcomeHome = lazy(() => import("./pages/welcomeHome/WelcomeHome"));
const PageMaintenance = lazy(() =>
  import("./pages/page-maintenance/PageMaintenance")
);
const Layout = lazy(() => import("./components/layout/Layout"));

const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

function App() {
  const { user, isAuthenticated } = useAuth0();
  const location = useLocation();

  // ID del usuario que puede usar ElevenLabs
  const ELEVENLABS_USER_ID = "auth0|6871bacd71a0a83edb950d47";

  // Verificar si la ruta actual es una ruta de dashboard
  const isDashboardRoute = () => {
    const currentPath = location.pathname;

    // Ruta principal (/)
    if (currentPath === "/") return true;

    // Rutas de dashboard definidas en dashboardRoutes
    const dashboardPaths = dashboardRoutes.map(route => `/${route.path}`);

    // Verificar si la ruta actual coincide con alguna ruta de dashboard
    return dashboardPaths.some(path => currentPath.startsWith(path));
  };

  // Determinar qué widget mostrar según el usuario y la ruta
  const shouldShowElevenLabs = isAuthenticated && isDashboardRoute();
  const shouldShowGHL = isAuthenticated && isDashboardRoute();

  //permite guardar el subdominio en session storage
  useEffect(() => {
    sessionStorage.setItem("sd", getOrgName());
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const roles = (user?.["https://wherebyfront.vercel.app/roles"] || []).map(
      (r) => r.toUpperCase()
    );
    const isAdmin = roles.includes(superAdminRoleName.toUpperCase());
    if (!isAdmin && localStorage.getItem("guest")) {
      localStorage.removeItem("guest");
      window.location.reload();
    }
  }, [user]);

  return (
    <>
      {/* Widget de ElevenLabs - Solo para usuario específico */}
      {shouldShowElevenLabs && <ElevenLabsSDKWidget />}

      {/* Widget de GoHighLevel - Para todos los demás usuarios */}
      {/* {shouldShowGHL && <GHLWidget />} */}
      <Routes>
        {MAINTENANCE_MODE ? (
          <>
            <Route
              path="/maintenance"
              element={
                <Suspense fallback={<LoadingPxch />}>
                  <PageMaintenance />
                </Suspense>
              }
            />
            <Route
              exact
              path="/login"
              element={
                <Suspense fallback={<LoadingPxch />}>
                  <Login />
                </Suspense>
              }
            />
            <Route path="/logout" element={<LogoutWLS />} />
            {routes?.map((route) => {
              return (
                <Route
                  key={route?.path}
                  exact
                  path={route?.path}
                  element={
                    <Suspense fallback={<LoadingPxch />}>
                      <ProtectedRoute
                        isAllowed={route?.grants ? route?.grants : true}
                        active={route?.active !== undefined ? route?.active : true}
                      >
                        {route?.Component}
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
              );
            })}
            <Route path="*" element={<Navigate to="/maintenance" replace />} />
          </>
        ) : (
          <>
        <Route
          exact
          path="/login"
          element={
            <Suspense fallback={<LoadingPxch />}>
              <Login />
            </Suspense>
          }
        />
        <Route path="/forbidden" element={<Forbidden />} />

        <Route path="/*" element={<PageNotFound />} />

        <Route
          path="/welcomeHome"
          element={
            <Suspense fallback={<LoadingPxch />}>
              <WelcomeHome />
            </Suspense>
          }
        />

        <Route path="/logout" element={<LogoutWLS />} />
        <Route
          path="/checkout-information"
          element={
            <Suspense fallback={<LoadingPxch />}>
              <LoginWLS />
            </Suspense>
          }
        />
        <Route
          path="/maintenance"
          element={
            <Suspense fallback={<LoadingPxch />}>
              <PageMaintenance />
            </Suspense>
          }
        />

        <Route
          path="/verify"
          element={
            <Suspense fallback={<LoadingPxch />}>
              <VerifyInvite1 />
            </Suspense>
          }
        />

        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingPxch />}>
              <Layout active={true} />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<LoadingPxch />}>
                <ProtectedRoute redirectOnHold isAllowed active redirectOnRoles={wlsNoMeetService} redirectUrl={"/my-rewards"}>
                  <DashboardMain />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            exact
            path="/on-hold"
            element={
              <Suspense fallback={<LoadingPxch />}>
                <AccountOnHold />
              </Suspense>
            }
          />
          <Route
            exact
            path="/dissabled"
            element={
              <Suspense fallback={<LoadingPxch />}>
                <AccountDisabled />
              </Suspense>
            }
          />
          <Route
            exact
            path="/free-ends"
            element={
              <Suspense fallback={<LoadingPxch />}>
                <AccountTrialEnds />
              </Suspense>
            }
          />
          {dashboardRoutes?.map((route) => (
            <Route
              key={route?.path}
              exact
              path={route?.path}
              element={
                <Suspense fallback={<LoadingPxch />}>
                  <ProtectedRoute
                    isAllowed={route?.grants ? route?.grants : true}
                    redirectOnHold={route?.redirectOnHold || false}
                    active={route?.active !== undefined ? route?.active : true}
                    roles={route?.roles}
                    notRoles={route?.notRoles}
                    redirectUrl={route?.redirectUrl}
                  >
                    {route?.Component}
                  </ProtectedRoute>
                </Suspense>
              }
            />
          ))}
        </Route>

        {routes?.map((route) => {
          return (
            <Route
              key={route?.path}
              exact
              path={route?.path}
              element={
                <Suspense fallback={<LoadingPxch />}>
                  <ProtectedRoute
                    isAllowed={route?.grants ? route?.grants : true}
                    active={route?.active !== undefined ? route?.active : true}
                  >
                    {route?.Component}
                  </ProtectedRoute>
                </Suspense>
              }
            />
          );
        })}
          </>
        )}
      </Routes>
    </>
  );
}

export default App;
